import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { SelectInput } from "~/components";
import {
  FormContainer,
  LaurenQuoteMobile,
  NextButton,
  Prompt,
  QuestionContainer,
  QuestionPageContainer,
  RadioInput,
} from "~/OnboardingApp/components";
import { useOnboarding } from "~/OnboardingApp/hooks/useOnboarding";
import { useSubmitOnboardingQuestion } from "~/OnboardingApp/hooks/useSubmitOnboardingQuestion";
import { usePartnersIndexQuery } from "~/store/api";
import { requiredString } from "~/utils/validations";

export type ClinicRefer = "" | "Yes" | "No";

type Values = {
  clinicRefer: ClinicRefer;
  clinicReferName: string;
};

const values: ClinicRefer[] = ["Yes", "No"];

const validationSchema = Yup.object({
  clinicRefer: requiredString.oneOf(values),
  clinicReferName: Yup.string(),
});

const queryArgs = { type: "active" as const };

export const ClinicReferPage: FC = () => {
  const { t } = useTranslation();
  const { onSubmit } = useSubmitOnboardingQuestion();
  const { clinicRefer, clinicReferName } = useOnboarding();
  const { data: partnersData, isLoading: partnersLoading } =
    usePartnersIndexQuery(queryArgs);

  const initialValues: Values = {
    clinicRefer: clinicRefer || "",
    clinicReferName: clinicReferName || "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  const showAdditionalQuestion = formik?.values?.clinicRefer === "Yes";
  const isAutoAdvance = (option: string) => option === "No";
  const commentCopy = t("onboarding.clinicReferPage.comment");
  const partnerOptions =
    partnersData?.data?.map(
      (partner: { attributes: { name: string; slug: string } }) => ({
        label: partner.attributes.name,
        value: partner.attributes.slug,
      }),
    ) || [];

  // Get current values - prioritize formik values for immediate updates, fallback to persisted values
  const currentClinicRefer = formik.values.clinicRefer || clinicRefer;
  const currentClinicReferName =
    formik.values.clinicReferName || clinicReferName;

  // Show button when: user answered clinic refer AND (answered No OR selected a partner)
  const showButton =
    currentClinicRefer === "No" ||
    (showAdditionalQuestion && !!currentClinicReferName);

  return (
    <QuestionPageContainer>
      <FormContainer formik={formik}>
        <QuestionContainer>
          <Prompt
            comment={commentCopy}
            question={t("onboarding.clinicReferPage.question")}
          />
          {commentCopy && (
            <LaurenQuoteMobile initialShow>{commentCopy}</LaurenQuoteMobile>
          )}

          <RadioInput
            autoAdvance={isAutoAdvance}
            id="clinicRefer"
            values={values}
            defaultValue={clinicRefer}
          />
        </QuestionContainer>

        {showAdditionalQuestion && (
          <QuestionContainer>
            <Prompt
              question={t(
                "onboarding.clinicReferPage.additionalQuestion.question",
              )}
            />
            <SelectInput
              id="clinicReferName"
              placeholder={t(
                "onboarding.clinicReferPage.additionalQuestion.placeholder",
              )}
              options={partnerOptions}
              isDisabled={partnersLoading}
            />
          </QuestionContainer>
        )}

        <NextButton showButton={showButton} />
      </FormContainer>
    </QuestionPageContainer>
  );
};
